import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Chip,
  Grid,
  Divider,
  Input,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useForm, Controller } from 'react-hook-form';
import { bankApi, BankTransaction, ReconciliationData, ReconcileTransactionData } from '../services/bank';
import { paymentsApi } from '../services/payments';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Bank: React.FC = () => {
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [unreconcileDialogOpen, setUnreconcileDialogOpen] = useState(false);
  const [reconcilingTransaction, setReconcilingTransaction] = useState<BankTransaction | null>(null);
  const [viewingTransaction, setViewingTransaction] = useState<BankTransaction | null>(null);
  const [unreconcilingTransaction, setUnreconcilingTransaction] = useState<BankTransaction | null>(null);
  const [reconciliationData, setReconciliationData] = useState<ReconciliationData | null>(null);
  const [uploading, setUploading] = useState(false);
  const [filterTab, setFilterTab] = useState(0); // 0: all, 1: unreconciled, 2: reconciled
  const [accountName, setAccountName] = useState('');
  const [statementFile, setStatementFile] = useState<File | null>(null);

  const reconcileForm = useForm<ReconcileTransactionData>({
    defaultValues: {
      invoiceId: '',
      paymentId: '',
      expenseId: '',
      category: '',
      isOpeningBalance: false,
      createAdhocPayment: false,
      adhocPaymentDescription: '',
      adhocPaymentReference: '',
    },
  });

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let reconciled: boolean | undefined;
      if (filterTab === 1) reconciled = false;
      else if (filterTab === 2) reconciled = true;
      
      const transactionsData = await bankApi.getTransactions(reconciled);
      setTransactions(transactionsData || []);
    } catch (err: any) {
      console.error('Error loading transactions:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load transactions';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filterTab]);

  const loadReconciliationData = useCallback(async () => {
    try {
      const data = await bankApi.getReconciliationData();
      setReconciliationData(data);
    } catch (err) {
      console.error('Error loading reconciliation data:', err);
    }
  }, []);

  useEffect(() => {
    loadTransactions();
    loadReconciliationData();
  }, [loadTransactions, loadReconciliationData]);

  const handleOpenUploadDialog = () => {
    setAccountName('');
    setStatementFile(null);
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setAccountName('');
    setStatementFile(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const allowedExtensions = ['.csv', '.xml', '.txt', '.pdf'];
      const isValidFile = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValidFile) {
        setError('Please select a supported file format: CSV, XML (camt.053), TXT (MT940), or PDF');
        return;
      }
      setStatementFile(file);
      setError(null);
    }
  };

  const handleUploadStatement = async () => {
    if (!statementFile || !accountName.trim()) {
      setError('Please select a bank statement file and enter an account name');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const result = await bankApi.uploadStatement(statementFile, accountName);
      setSuccess(`Successfully imported ${result.count} transactions`);
      handleCloseUploadDialog();
      loadTransactions();
      loadReconciliationData();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error uploading statement:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to upload statement';
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleOpenReconcileDialog = async (transaction: BankTransaction) => {
    setReconcilingTransaction(transaction);
    await loadReconciliationData();
    reconcileForm.reset({
      invoiceId: transaction.invoiceId || '',
      paymentId: transaction.paymentId || '',
      expenseId: transaction.expenseId || '',
      category: transaction.category || '',
      isOpeningBalance: transaction.category === 'Opening Balance',
    });
    setReconcileDialogOpen(true);
  };

  const handleCloseReconcileDialog = () => {
    setReconcileDialogOpen(false);
    setReconcilingTransaction(null);
    reconcileForm.reset();
  };

  const handleReconcileTransaction = async (data: ReconcileTransactionData) => {
    if (!reconcilingTransaction) return;

    // At least one of invoiceId, paymentId, expenseId, category, isOpeningBalance, or createAdhocPayment must be provided
    if (!data.invoiceId && !data.paymentId && !data.expenseId && !data.category && !data.isOpeningBalance && !data.createAdhocPayment) {
      setError('Please select an invoice, payment, expense, enter a category, mark as opening balance, or create an ad-hoc payment');
      return;
    }

    // Validate ad-hoc payment fields if creating one
    if (data.createAdhocPayment) {
      if (!data.adhocPaymentDescription?.trim()) {
        setError('Please provide a description for the ad-hoc payment');
        return;
      }
    }

    try {
      setError(null);
      
      let paymentId = data.paymentId;

      // Create ad-hoc payment if requested
      if (data.createAdhocPayment && reconcilingTransaction.amount > 0) {
        const newPayment = await paymentsApi.create({
          amount: Math.abs(reconcilingTransaction.amount),
          paymentDate: reconcilingTransaction.transactionDate.split('T')[0], // Extract date part
          method: 'bank_transfer',
          direction: 'incoming',
          status: 'sent',
          reference: data.adhocPaymentReference || reconcilingTransaction.reference || undefined,
          notes: data.adhocPaymentDescription,
        });
        paymentId = newPayment.id;
      }

      const reconcileData: ReconcileTransactionData = {};
      if (data.invoiceId) reconcileData.invoiceId = data.invoiceId;
      if (paymentId) reconcileData.paymentId = paymentId;
      if (data.expenseId) reconcileData.expenseId = data.expenseId;
      if (data.category) reconcileData.category = data.category;
      if (data.isOpeningBalance) reconcileData.isOpeningBalance = true;

      await bankApi.reconcileTransaction(reconcilingTransaction.id, reconcileData);
      setSuccess('Transaction reconciled successfully');
      handleCloseReconcileDialog();
      loadTransactions();
      loadReconciliationData();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reconcile transaction';
      setError(errorMessage);
    }
  };

  const handleOpenViewDialog = async (transaction: BankTransaction) => {
    try {
      const fullTransaction = await bankApi.getTransactionById(transaction.id);
      setViewingTransaction(fullTransaction);
      setViewDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load transaction details');
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingTransaction(null);
  };

  const handleOpenUnreconcileDialog = (transaction: BankTransaction) => {
    setUnreconcilingTransaction(transaction);
    setUnreconcileDialogOpen(true);
  };

  const handleCloseUnreconcileDialog = () => {
    setUnreconcileDialogOpen(false);
    setUnreconcilingTransaction(null);
  };

  const handleUnreconcileTransaction = async () => {
    if (!unreconcilingTransaction) return;

    try {
      setError(null);
      await bankApi.unreconcileTransaction(unreconcilingTransaction.id);
      setSuccess('Transaction unreconciled successfully');
      handleCloseUnreconcileDialog();
      loadTransactions();
      loadReconciliationData();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to unreconcile transaction';
      setError(errorMessage);
    }
  };

  const getReconciliationType = (transaction: BankTransaction): string => {
    if (transaction.invoiceId) return 'Invoice';
    if (transaction.paymentId) return 'Payment';
    if (transaction.expenseId) return 'Expense';
    if (transaction.category === 'Opening Balance') return 'Opening Balance';
    if (transaction.category) return 'Category';
    return 'None';
  };

  const getReconciliationDescription = (transaction: BankTransaction): string => {
    if (transaction.invoiceNumber) {
      return `Invoice ${transaction.invoiceNumber} (${formatCurrency(transaction.invoiceTotal || 0, transaction.invoiceCurrency || 'USD')})`;
    }
    if (transaction.paymentAmount) {
      return `Payment ${formatCurrency(transaction.paymentAmount, transaction.paymentCurrency || 'USD')}`;
    }
    if (transaction.expenseDescription) {
      return `${transaction.expenseDescription} (${formatCurrency(transaction.expenseAmount || 0, transaction.expenseCurrency || 'USD')})`;
    }
    if (transaction.category === 'Opening Balance') {
      return 'Opening Balance';
    }
    if (transaction.category) {
      return `Category: ${transaction.category}`;
    }
    return 'Not reconciled';
  };

  const getUnreconciledCount = () => {
    return transactions.filter(t => !t.reconciled).length;
  };

  const getReconciledCount = () => {
    return transactions.filter(t => t.reconciled).length;
  };

  if (loading && transactions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Bank Reconciliation</Typography>
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={handleOpenUploadDialog}
        >
          Upload Statement
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={filterTab} onChange={(e, newValue) => setFilterTab(newValue)}>
          <Tab label={`All (${transactions.length})`} />
          <Tab label={`Unreconciled (${getUnreconciledCount()})`} />
          <Tab label={`Reconciled (${getReconciledCount()})`} />
        </Tabs>
      </Paper>

      {/* Transactions Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reconciled With</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">
                      {filterTab === 0 
                        ? 'No transactions found. Upload a bank statement to get started.'
                        : filterTab === 1
                        ? 'No unreconciled transactions.'
                        : 'No reconciled transactions.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {format(parseISO(transaction.transactionDate), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>{transaction.accountName}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{transaction.description}</Typography>
                      {transaction.reference && (
                        <Typography variant="caption" color="textSecondary">
                          Ref: {transaction.reference}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(Math.abs(transaction.amount), 'USD')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transaction.balance !== null && transaction.balance !== undefined
                        ? formatCurrency(transaction.balance, 'USD')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {transaction.reconciled ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Reconciled"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Unreconciled"
                          size="small"
                          color="default"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {transaction.reconciled ? (
                        <Typography variant="caption">
                          {getReconciliationDescription(transaction)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenViewDialog(transaction)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {transaction.reconciled ? (
                          <Tooltip title="Unreconcile">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleOpenUnreconcileDialog(transaction)}
                            >
                              <LinkOffIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reconcile">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenReconcileDialog(transaction)}
                            >
                              <LinkIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Upload Statement Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Bank Statement</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Account Name"
              required
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="e.g., Business Checking Account"
            />
            <Box>
              <InputLabel sx={{ mb: 1 }}>Bank Statement File</InputLabel>
              <Input
                type="file"
                inputProps={{ accept: '.csv,.xml,.txt,.pdf' }}
                onChange={handleFileChange}
                fullWidth
              />
              {statementFile && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Selected: {statementFile.name}
                </Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Supported Formats:
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div" sx={{ display: 'block', mb: 0.5 }}>
                  • <strong>CSV</strong> - Comma-separated values (date, description, amount, balance, reference)
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div" sx={{ display: 'block', mb: 0.5 }}>
                  • <strong>XML</strong> - camt.053 format (ISO 20022) - V02, V08, or V12
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div" sx={{ display: 'block', mb: 0.5 }}>
                  • <strong>TXT</strong> - MT940 format (SWIFT statement)
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div" sx={{ display: 'block' }}>
                  • <strong>PDF</strong> - Bank statement PDF (limited parsing support)
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Revolut Users:</strong> Download your account statement from Revolut app in any of these formats for best results.
                  </Typography>
                </Alert>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Cancel</Button>
          <Button
            onClick={handleUploadStatement}
            variant="contained"
            disabled={!statementFile || !accountName.trim() || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconcile Transaction Dialog */}
      <Dialog open={reconcileDialogOpen} onClose={handleCloseReconcileDialog} maxWidth="md" fullWidth>
        <form onSubmit={reconcileForm.handleSubmit(handleReconcileTransaction)}>
          <DialogTitle>Reconcile Transaction</DialogTitle>
          <DialogContent>
            {reconcilingTransaction && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary">Transaction Details</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Date:</strong> {format(parseISO(reconcilingTransaction.transactionDate), 'MMM d, yyyy')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Amount:</strong> {formatCurrency(Math.abs(reconcilingTransaction.amount), 'USD')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Description:</strong> {reconcilingTransaction.description}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2">Match with:</Typography>
              
              <Controller
                name="invoiceId"
                control={reconcileForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Invoice (Optional)</InputLabel>
                    <Select {...field} label="Invoice (Optional)">
                      <MenuItem value="">None</MenuItem>
                      {reconciliationData?.invoices.map((invoice) => (
                        <MenuItem key={invoice.id} value={invoice.id}>
                          {invoice.invoiceNumber} - {formatCurrency(invoice.total, invoice.currency)} ({format(parseISO(invoice.issueDate), 'MMM d, yyyy')})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="paymentId"
                control={reconcileForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Payment (Optional)</InputLabel>
                    <Select {...field} label="Payment (Optional)">
                      <MenuItem value="">None</MenuItem>
                      {reconciliationData?.payments.map((payment) => (
                        <MenuItem key={payment.id} value={payment.id}>
                          {formatCurrency(payment.amount, payment.currency)} ({format(parseISO(payment.paymentDate), 'MMM d, yyyy')})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="expenseId"
                control={reconcileForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Expense (Optional)</InputLabel>
                    <Select {...field} label="Expense (Optional)">
                      <MenuItem value="">None</MenuItem>
                      {reconciliationData?.expenses.map((expense) => (
                        <MenuItem key={expense.id} value={expense.id}>
                          {expense.description} - {formatCurrency(expense.amount, expense.currency)} ({format(parseISO(expense.expenseDate), 'MMM d, yyyy')})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              {/* Create Ad-hoc Payment Option (for incoming transactions) */}
              {reconcilingTransaction && reconcilingTransaction.amount > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Controller
                    name="createAdhocPayment"
                    control={reconcileForm.control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value || false}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              // If creating ad-hoc payment is checked, clear other selections
                              if (e.target.checked) {
                                reconcileForm.setValue('invoiceId', '');
                                reconcileForm.setValue('paymentId', '');
                                reconcileForm.setValue('expenseId', '');
                                reconcileForm.setValue('category', '');
                                reconcileForm.setValue('isOpeningBalance', false);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            <strong>Create Ad-hoc Incoming Payment</strong>
                          </Typography>
                        }
                      />
                    )}
                  />
                  {reconcileForm.watch('createAdhocPayment') && (
                    <Box sx={{ ml: 4, mt: 1 }}>
                      <Controller
                        name="adhocPaymentDescription"
                        control={reconcileForm.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Payment Description"
                            required
                            placeholder="e.g., Consulting fee, Refund, etc."
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                      <Controller
                        name="adhocPaymentReference"
                        control={reconcileForm.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Payment Reference (Optional)"
                            placeholder="e.g., Reference number, Invoice number, etc."
                            defaultValue={reconcilingTransaction.reference || ''}
                          />
                        )}
                      />
                    </Box>
                  )}
                </>
              )}

              {/* Opening Balance Option */}
              {reconciliationData?.openingBalance && !reconciliationData.openingBalance.reconciled && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Controller
                    name="isOpeningBalance"
                    control={reconcileForm.control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value || false}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              // If opening balance is checked, clear other selections
                              if (e.target.checked) {
                                reconcileForm.setValue('invoiceId', '');
                                reconcileForm.setValue('paymentId', '');
                                reconcileForm.setValue('expenseId', '');
                                reconcileForm.setValue('category', '');
                                reconcileForm.setValue('createAdhocPayment', false);
                              }
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              <strong>Opening Balance</strong>
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Reconcile with company opening balance: {reconciliationData.openingBalance ? formatCurrency(reconciliationData.openingBalance.amount, reconciliationData.openingBalance.currency || 'USD') : ''}
                            </Typography>
                          </Box>
                        }
                      />
                    )}
                  />
                </>
              )}

              <Controller
                name="category"
                control={reconcileForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Category (Optional)"
                    placeholder="e.g., Office Supplies, Utilities"
                    disabled={reconcileForm.watch('isOpeningBalance')}
                  />
                )}
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                Select at least one: invoice, payment, expense, opening balance, create ad-hoc payment, or category to reconcile this transaction.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReconcileDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Reconcile
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Transaction Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {viewingTransaction && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Date</Typography>
                  <Typography variant="body1">
                    {format(parseISO(viewingTransaction.transactionDate), 'MMM d, yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Account</Typography>
                  <Typography variant="body1">{viewingTransaction.accountName}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Typography variant="body1">{viewingTransaction.description}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                  <Typography variant="h6" fontWeight="bold" color={viewingTransaction.amount >= 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(Math.abs(viewingTransaction.amount), 'USD')}
                  </Typography>
                </Grid>
                {viewingTransaction.balance !== null && viewingTransaction.balance !== undefined && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Balance</Typography>
                    <Typography variant="body1">
                      {formatCurrency(viewingTransaction.balance, 'USD')}
                    </Typography>
                  </Grid>
                )}
                {viewingTransaction.reference && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Reference</Typography>
                    <Typography variant="body1">{viewingTransaction.reference}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  {viewingTransaction.reconciled ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Reconciled"
                      size="small"
                      color="success"
                      sx={{ mt: 1 }}
                    />
                  ) : (
                    <Chip
                      icon={<CancelIcon />}
                      label="Unreconciled"
                      size="small"
                      color="default"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Grid>
                {viewingTransaction.reconciled && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Reconciled With</Typography>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {getReconciliationDescription(viewingTransaction)}
                      </Typography>
                    </Grid>
                    {viewingTransaction.category && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                        <Typography variant="body1">{viewingTransaction.category}</Typography>
                      </Grid>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Created: {format(parseISO(viewingTransaction.createdAt), 'MMM d, yyyy HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Unreconcile Confirmation Dialog */}
      <Dialog open={unreconcileDialogOpen} onClose={handleCloseUnreconcileDialog}>
        <DialogTitle>Unreconcile Transaction</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unreconcile this transaction?
            <br />
            <br />
            {unreconcilingTransaction && (
              <>
                <strong>Date:</strong> {format(parseISO(unreconcilingTransaction.transactionDate), 'MMM d, yyyy')}
                <br />
                <strong>Amount:</strong> {formatCurrency(Math.abs(unreconcilingTransaction.amount), 'USD')}
                <br />
                <strong>Description:</strong> {unreconcilingTransaction.description}
                <br />
                <br />
              </>
            )}
            This will remove the link to the invoice, payment, expense, or category.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnreconcileDialog}>Cancel</Button>
          <Button onClick={handleUnreconcileTransaction} color="warning" variant="contained">
            Unreconcile
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bank;
